// Oturum durumu. K1: oturum TESİS bazlıdır — jeton tek başına yetmez, hangi
// tesisin kapsamında olduğumuz da taşınır.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, jetonAyarla, oturumDustuKancasi } from './api'
import { toast } from '../components/Toast'

export type Kullanici = {
  id: string
  eposta: string
  ad: string
  unvan: string | null
  rol: string
  yetkiOnay: boolean
  yetkiStorno: boolean
}

export type Tesis = {
  id: string
  kod: string
  ad: string
  il: string | null
  aktif: boolean
}

export type GirisYanit = {
  kullanici: Kullanici
  tesisler: Tesis[]
  gecisJetonu: string
  /**
   * Süper adminde true: tesis listesi BOŞ gelir ve 2. adım (tesis seçimi)
   * atlanır. Bayrak olmadan boş liste "tesisiniz yok" hatası sanılırdı.
   */
  sistemYoneticisi?: boolean
}

export type SistemJetonYanit = {
  jwt: string
  refresh: string
  bitis: string
  kullanici: Kullanici
}

export type JetonYanit = {
  jwt: string
  refresh: string
  bitis: string
  tesis: Tesis
  /** Modül → "yok" | "oku" | "yaz" (ekran 14 rol izin matrisi). */
  izinler: Record<string, string>
  yetkiOnay: boolean
  yetkiStorno: boolean
  /** Şirket unvanı — kabuğun üst çubuğu ve durum çubuğu bunu yazıyor. */
  tenantUnvan: string
}

type Durum = {
  jwt: string
  kullanici: Kullanici
  /**
   * Süper adminde tesis YOK. `null` bilinçli: boş bir sahte tesis nesnesi
   * kabuğun tesis seçicisini doldurup olmayan bir kapsam varmış gibi gösterirdi.
   */
  tesis: Tesis | null
  /**
   * Şirket unvanı. Sunucudan geliyor; ÖNCEDEN hiç doldurulmuyordu ve kabuk
   * sabit yazılmış bir yedeğe ("Yeşil Enerji A.Ş.") düşüyordu — her müşteri
   * demo şirketin adını görüyordu.
   */
  tenantUnvan?: string
  izinler: Record<string, string>
  yetkiOnay: boolean
  yetkiStorno: boolean
}

const ANAHTAR = 'btys.oturum'

type Baglam = {
  durum: Durum | null
  girisYap: (eposta: string, sifre: string) => Promise<GirisYanit>
  tesisSec: (gecisJetonu: string, kullanici: Kullanici, tesisId: string) => Promise<void>
  /** Süper adminin 2. adımı — seçilecek tesis yok. */
  sistemeGir: (gecisJetonu: string) => Promise<void>
  cikisYap: () => Promise<void>
  /**
   * Kullanıcı bilgilerini sunucudan tazeler. Profil ekranında ad/ünvan
   * değişince kabuktaki avatar ve üst bar da güncellensin diye.
   */
  tazele: () => Promise<void>
  /**
   * Çıkış onay drawer'ını açar (referans <c>cikisOnay()</c>). Kabuk seviyesinde
   * duruyor: hem kullanıcı menüsünden hem ekran 13'ün "Çıkış yap" düğmesinden
   * aynı katman açılıyor.
   */
  cikisOnayiAc: () => void
  /** Kabuk bu bayrağı dinleyip drawer'ı açıyor. */
  cikisOnayiAcik: boolean
  cikisOnayiKapat: () => void
  /**
   * İzin haritası sunucudan tazelenemedi mi?
   *
   * Sessiz kalmak PAHALIYA MAL OLDU: harita localStorage'da saklandığı için
   * yeni bir modül eklendiğinde eski oturumun haritasında o anahtar yok ve
   * menü öğesi GÖRÜNMÜYOR. Tazeleme başarısızsa (API kapalı, ağ koptu)
   * kullanıcı eksik menüyü görüyor ama nedenini göremiyordu.
   */
  izinlerTazelenemedi: boolean
  /** Durum çubuğundaki uyarıdan elle yeniden dener. */
  izinleriTazele: () => Promise<void>
}

const OturumBaglami = createContext<Baglam | null>(null)

function oku(): Durum | null {
  try {
    const ham = localStorage.getItem(ANAHTAR)
    const d = ham ? (JSON.parse(ham) as Durum) : null
    // Jetonu BURADA kuruyoruz, useEffect'te değil. Efektler çocuklar render
    // edildikten SONRA çalışır; ilk sorgular o ana kadar Authorization başlığı
    // olmadan gidip 401 alıyordu. TanStack'in retry'ı bunu gizliyordu ama her
    // açılışta bir hatalı istek ve kirli konsol üretiyordu.
    jetonAyarla(d?.jwt ?? null)
    return d
  } catch {
    jetonAyarla(null)
    return null
  }
}

export function OturumSaglayici({ children }: { children: React.ReactNode }) {
  const [durum, setDurum] = useState<Durum | null>(oku)
  const [cikisOnayiAcik, setCikisOnayiAcik] = useState(false)
  const [izinlerTazelenemedi, setIzinlerTazelenemedi] = useState(false)

  // Sunucu oturumu reddederse yerelde de kapat. Yoksa kabuk açık kalıyor,
  // her sorgu 401 alıyor ve ekranlar bunu "veri yok" diye gösteriyordu —
  // kullanıcı jetonunun düştüğünü anlamadan boş tablolara bakıyordu.
  // Durum null olunca Korumali /giris'e yönlendiriyor.
  useEffect(() => {
    oturumDustuKancasi(() => {
      if (!localStorage.getItem(ANAHTAR)) return // zaten kapalı — tekrar uyarma
      toast('Oturumun sona erdi — yeniden giriş yapın.')
      setDurum(null)
    })
    return () => oturumDustuKancasi(null)
  }, [])

  // İzin haritası localStorage'da SAKLANIYOR. Yeni bir modül eklendiğinde
  // (ör. Faz 3'te "Uniteler") eski oturumun haritasında o anahtar olmuyor ve
  // kullanıcı yeni ekranda "erişim yetkiniz yok" görüyor — canlıda tam bunu
  // yaşadık. Açılışta sunucudan tazeleyip üzerine yazıyoruz; tek küçük istek,
  // her yeni modül için kendiliğinden çalışır.
  const izinleriCek = useCallback(async () => {
    const y = await api<{
      izinler: Record<string, string>
      yetkiOnay: boolean
      yetkiStorno: boolean
      tenantUnvan?: string
    }>('/api/auth/izinler')

    // Unvan burada da güncelleniyor: bu uç her açılışta çağrılıyor, böylece
    // unvan alanı eklenmeden ÖNCE açılmış oturumlar yeniden giriş gerektirmeden
    // kendiliğinden düzeliyor.
    setDurum((d) =>
      d === null ||
      (JSON.stringify(d.izinler) === JSON.stringify(y.izinler) &&
        d.yetkiOnay === y.yetkiOnay &&
        d.yetkiStorno === y.yetkiStorno &&
        (!y.tenantUnvan || d.tenantUnvan === y.tenantUnvan))
        ? d
        : {
            ...d,
            izinler: y.izinler,
            yetkiOnay: y.yetkiOnay,
            yetkiStorno: y.yetkiStorno,
            tenantUnvan: y.tenantUnvan || d.tenantUnvan,
          },
    )
    setIzinlerTazelenemedi(false)
  }, [])

  /** Durum çubuğundaki uyarıdan elle yeniden deneme. */
  const izinleriTazele = useCallback(async () => {
    try {
      await izinleriCek()
      toast('Yetki haritası tazelendi — menü güncel.')
    } catch {
      toast('Yetki haritası hâlâ tazelenemiyor — sunucuya ulaşılamıyor.')
    }
  }, [izinleriCek])

  useEffect(() => {
    if (!durum) return
    let iptal = false

    izinleriCek()
      // Ağ hatası oturumu DÜŞÜRMEMELİ: saklanan harita ile devam edilir.
      // Ama sessiz de kalmamalı — eski haritada yeni modülün anahtarı yok ve
      // menü öğesi görünmüyor; kullanıcı eksikliği görüp nedenini göremiyordu.
      .catch(() => {
        if (!iptal) setIzinlerTazelenemedi(true)
      })

    return () => { iptal = true }
    // Yalnız oturum kimliği değişince tazele — her render'da değil.
  }, [durum?.jwt, izinleriCek])

  // Sonraki değişimler (giriş / çıkış) için eşitleme ve kalıcılık.
  useEffect(() => {
    jetonAyarla(durum?.jwt ?? null)
    if (durum) localStorage.setItem(ANAHTAR, JSON.stringify(durum))
    else localStorage.removeItem(ANAHTAR)
  }, [durum])

  const girisYap = useCallback(
    (eposta: string, sifre: string) =>
      api<GirisYanit>('/api/auth/giris', { yontem: 'POST', govde: { eposta, sifre } }),
    [],
  )

  const tesisSec = useCallback(
    async (gecisJetonu: string, kullanici: Kullanici, tesisId: string) => {
      const yanit = await api<JetonYanit>('/api/auth/tesis-sec', {
        yontem: 'POST',
        govde: { tesisId },
        jeton: gecisJetonu,
      })
      // Jeton BURADA kuruluyor, aşağıdaki eşitleme efektinde değil.
      //
      // Efektler tanım sırasıyla çalışıyor: izin haritasını tazeleyen efekt,
      // jetonu `api`'ye yazan efektten ÖNCE geliyor. Girişte oturum yeni
      // doğduğu için `_jeton` henüz null; o ilk `/api/auth/izinler` isteği
      // Authorization başlığı OLMADAN gidiyor, 401 "oturum_gecersiz" alıyor ve
      // 401 kancası oturumu kapatıp kullanıcıyı /giris'e geri atıyordu —
      // "giriş yapınca tekrar giriş ekranına düşüyorum" tam olarak buydu.
      //
      // `oku()` sayfa açılışında aynı şeyi zaten yapıyor; eksik olan tek yer
      // buydu (tesis-sec kendi jetonunu parametreyle gönderdiği için `_jeton`
      // hiç güncellenmiyordu).
      jetonAyarla(yanit.jwt)

      setDurum({
        jwt: yanit.jwt,
        kullanici,
        tesis: yanit.tesis,
        tenantUnvan: yanit.tenantUnvan,
        izinler: yanit.izinler,
        yetkiOnay: yanit.yetkiOnay,
        yetkiStorno: yanit.yetkiStorno,
      })
    },
    [],
  )

  /**
   * Süper admin oturumu. `tesisSec` ile aynı jeton disiplini: jeton BURADA
   * kuruluyor, efekt sırasına bırakılmıyor (bkz. tesisSec'teki not).
   *
   * İzin haritası BOŞ: süper admin tenant modüllerinin tamamına kapalı ve
   * kabuk menüsü bu haritadan çiziliyor — dolu bir harita ona giremeyeceği
   * ekranları gösterirdi.
   */
  const sistemeGir = useCallback(async (gecisJetonu: string) => {
    const yanit = await api<SistemJetonYanit>('/api/auth/sistem', {
      yontem: 'POST',
      jeton: gecisJetonu,
    })
    jetonAyarla(yanit.jwt)
    setDurum({
      jwt: yanit.jwt,
      kullanici: yanit.kullanici,
      tesis: null,
      izinler: {},
      yetkiOnay: false,
      yetkiStorno: false,
    })
  }, [])

  const cikisYap = useCallback(async () => {
    try {
      await api('/api/auth/cikis', { yontem: 'POST' })
    } catch {
      // Sunucu ulaşılamasa bile yerel oturum kapanmalı — kullanıcı "çıktım"
      // dediyse ekranda oturumu açık bırakmak yanlış olur.
    }
    setDurum(null)
  }, [])

  const tazele = useCallback(async () => {
    try {
      const p = await api<{ ad: string; unvan: string | null; rol: string }>('/api/profil')
      setDurum((d) =>
        d === null
          ? d
          : { ...d, kullanici: { ...d.kullanici, ad: p.ad, unvan: p.unvan } },
      )
    } catch {
      // Tazeleme kozmetik; başarısızsa saklanan bilgiyle devam edilir.
    }
  }, [])

  const cikisOnayiAc = useCallback(() => setCikisOnayiAcik(true), [])
  const cikisOnayiKapat = useCallback(() => setCikisOnayiAcik(false), [])

  const deger = useMemo<Baglam>(
    () => ({
      durum, girisYap, tesisSec, sistemeGir, cikisYap, tazele,
      cikisOnayiAc, cikisOnayiKapat, cikisOnayiAcik,
      izinlerTazelenemedi, izinleriTazele,
    }),
    [durum, girisYap, tesisSec, sistemeGir, cikisYap, tazele, cikisOnayiAc, cikisOnayiKapat, cikisOnayiAcik,
     izinlerTazelenemedi, izinleriTazele],
  )

  return <OturumBaglami.Provider value={deger}>{children}</OturumBaglami.Provider>
}

export function useOturum() {
  const b = useContext(OturumBaglami)
  if (!b) throw new Error('useOturum yalnız OturumSaglayici içinde kullanılabilir.')
  return b
}
